import { useEffect, useMemo, useState } from 'react';
import type { DeviceCombo } from '../store/wizardStore';
import { useWizardStore } from '../store/wizardStore';
import { NeutralQuestion } from './NeutralQuestion';
import { InfoHint } from './InfoHint';
import {
  loadCatalog, swatchHex, modulesByFunction, techsForFunction,
  techNeedsHub, techNeedsNeutral,
  SLOT_LABEL, SIZE_LABEL, TECH_LABEL,
  type Catalog, type EspelhoColor, type ModuleTech, type Modulo, type SlotFunction,
} from '../lib/espelhoCatalog';
import {
  resolveCombo, previewUrl, previewAvailable, SELECTABLE_FUNCTIONS,
  type ResolveResult, type NearestMatch,
} from '../lib/espelhoCombinator';

// Combinatorial espelho flow (Rafiq E3, 2026-06-23). Unlike the guided wizard —
// which only offers the ~101 stocked families — here the customer composes ANY
// size × per-slot-function × finish. The server says whether it is stocked or an
// "encomenda especial" and ranks the closest stocked alternatives (size +
// acabamento). A generator-backed preview renders even never-photographed
// combos; it degrades to a schematic glyph when the generator is unavailable
// (e.g. Cloud Run, where the donor library is not shipped).

type Step = 'tamanho' | 'config' | 'acabamento' | 'resultado' | 'modulos' | 'quantidade';
const STEP_ORDER: Step[] = ['tamanho', 'config', 'acabamento', 'resultado', 'modulos', 'quantidade'];
const STEP_LABEL: Record<Step, string> = {
  tamanho: 'Tamanho', config: 'Configuração', acabamento: 'Acabamento',
  resultado: 'Resultado', modulos: 'Módulos', quantidade: 'Quantidade',
};

const COLORS: EspelhoColor[] = ['Branco', 'Preto', 'Dourado'];

let comboSeq = 0;
const comboId = () => `combo-${Date.now().toString(36)}-${(comboSeq += 1)}`;

function techHint(t: ModuleTech): string {
  switch (t) {
    case 'wifi': return 'Liga ao router Wi-Fi. Sem hub.';
    case 'zigbee-sn': return 'Requer hub Zigbee. Não precisa de neutro.';
    case 'zigbee-cn': return 'Requer hub Zigbee. Requer neutro.';
    case 'zigbee': return 'Requer hub Zigbee.';
    case 'passivo': return 'Sem comunicação — interruptor tradicional.';
  }
}

// A chosen espelho — either the composed special-order or a stocked alternative.
interface Chosen {
  cartSku: string; // catalog sku (may be SYME) for in-stock; composed SYPE for special-order
  previewSku: string; // always canonical SYPE for the generator
  size: number;
  slotFunctions: SlotFunction[];
  color: EspelhoColor;
  inCatalog: boolean;
  label: string;
}

export function EspelhoCombinatorFlow({
  onSave,
  onClose,
}: {
  onSave: (combo: DeviceCombo) => void;
  onClose: () => void;
}) {
  const [cat, setCat] = useState<Catalog | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('tamanho');

  const [size, setSize] = useState<number | null>(null);
  const [slots, setSlots] = useState<SlotFunction[]>([]);
  const [color, setColor] = useState<EspelhoColor | null>(null);

  const [resolving, setResolving] = useState(false);
  const [result, setResult] = useState<ResolveResult | null>(null);
  const [chosen, setChosen] = useState<Chosen | null>(null);

  // moduleSel[i] = { tech, sku } | null per slot (moldura slots stay null).
  const [moduleSel, setModuleSel] = useState<({ tech: ModuleTech; sku: string } | null)[]>([]);
  const [qty, setQty] = useState(1);

  useEffect(() => { loadCatalog().then(setCat).catch(() => setErr('Não foi possível carregar o catálogo.')); }, []);

  const stepIndex = STEP_ORDER.indexOf(step);
  const allSlotsChosen = size != null && slots.length === size && slots.every(Boolean);

  function pickSize(s: number) {
    setSize(s);
    setSlots(new Array(s).fill('botoes-1'));
    setColor(null); setResult(null); setChosen(null); setModuleSel([]);
    setStep('config');
  }
  function setSlot(idx: number, fn: SlotFunction) {
    setSlots((prev) => { const next = [...prev]; next[idx] = fn; return next; });
  }

  async function goResolve(c: EspelhoColor) {
    if (size == null) return;
    setColor(c); setStep('resultado'); setResolving(true); setResult(null); setChosen(null);
    try {
      const r = await resolveCombo(size, slots, c, 2);
      setResult(r);
    } catch {
      setErr('Falha ao validar a configuração. Tenta novamente.');
    } finally {
      setResolving(false);
    }
  }

  function chooseComposed() {
    if (!result || size == null || color == null) return;
    setChosen({
      cartSku: result.requested.sku,
      previewSku: result.previewSku,
      size, slotFunctions: slots, color,
      inCatalog: result.availability === 'em-catalogo',
      label: result.exact?.name ?? `Espelho ${SIZE_LABEL[size]} à medida`,
    });
    enterModules(slots);
  }
  function chooseAlternative(m: NearestMatch) {
    const e = m.espelho;
    setChosen({
      cartSku: e.sku,
      previewSku: e.sku.replace(/^SYME/, 'SYPE'),
      size: e.size, slotFunctions: e.slotFunctions, color: e.color,
      inCatalog: true,
      label: e.name,
    });
    enterModules(e.slotFunctions);
  }
  function enterModules(sf: SlotFunction[]) {
    setModuleSel(sf.map(() => null));
    // Skip the module step entirely if every slot is a moldura (nothing to wire).
    if (sf.every((s) => s === 'moldura' || s === 'outro')) setStep('quantidade');
    else setStep('modulos');
  }

  function setModuleForSlot(idx: number, tech: ModuleTech, sku: string) {
    setModuleSel((prev) => { const next = [...prev]; next[idx] = { tech, sku }; return next; });
  }

  function confirm() {
    if (!chosen) return;
    const chosenModules = moduleSel.filter((m): m is { tech: ModuleTech; sku: string } => m != null);
    const moduleSkus = chosenModules.map((m) => m.sku);
    const kind: DeviceCombo['kind'] = chosen.slotFunctions.every((s) => s === 'moldura' || s === 'outro')
      ? 'tomada' : 'interruptor';
    const tag = chosen.inCatalog ? '' : ' · encomenda especial';
    // Combo-level protocol: a mixed-tech combo needs a hub if ANY slot is Zigbee
    // and needs neutral if ANY slot is "com neutro".
    const needsHub = chosenModules.some((m) => techNeedsHub(m.tech));
    const needsNeutral = chosenModules.some((m) => techNeedsNeutral(m.tech));
    const primaryTech = chosenModules.find((m) => techNeedsHub(m.tech))?.tech ?? chosenModules[0]?.tech ?? null;
    onSave({
      id: comboId(),
      kind,
      teclas: null,
      boxes: chosen.size as 1 | 2 | 3 | 4,
      color: chosen.color,
      label: `${chosen.label} · ${chosen.color}${tag}`,
      skus: [chosen.cartSku, ...moduleSkus],
      qty: Math.max(1, qty),
      tech: primaryTech,
      needsHub,
      needsNeutral,
    });
    onClose();
  }

  function back() {
    const order: Step[] = ['tamanho', 'config', 'acabamento', 'resultado', 'modulos', 'quantidade'];
    const i = order.indexOf(step);
    if (i > 0) setStep(order[i - 1]!);
  }

  return (
    <>
      <ol className="flex items-center gap-1 overflow-x-auto border-b border-smartify-line px-4 py-2">
        {STEP_ORDER.map((s, i) => (
          <li key={s} className="flex shrink-0 items-center gap-1">
            <span className={[
              'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              i === stepIndex ? 'bg-smartify-brand text-white'
                : i < stepIndex ? 'bg-smartify-brand/10 text-smartify-brand'
                : 'bg-smartify-paper text-smartify-muted',
            ].join(' ')}>
              <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-white/30 text-[9px]">{i + 1}</span>
              {STEP_LABEL[s]}
            </span>
            {i < STEP_ORDER.length - 1 && <span className="text-smartify-line">·</span>}
          </li>
        ))}
      </ol>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {err ? (
          <div className="rounded-smartify border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
        ) : !cat ? (
          <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center text-sm text-smartify-muted">A carregar…</div>
        ) : (
          <>
            {step === 'tamanho' && (
              <div>
                <p className="mb-3 text-sm text-smartify-muted">Quantas posições de aparelhagem?</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[1, 2, 3, 4].map((s) => (
                    <Card key={s} active={size === s} onClick={() => pickSize(s)}>
                      <div className="flex flex-col items-center gap-2 py-2">
                        <SlotGlyph n={s} />
                        <span className="font-semibold text-smartify-ink">{SIZE_LABEL[s]}</span>
                        <span className="text-xs text-smartify-muted">{s} {s === 1 ? 'posição' : 'posições'}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {step === 'config' && size != null && (
              <div className="space-y-4">
                <p className="text-sm text-smartify-muted">Escolhe a função de cada posição — qualquer combinação.</p>
                {slots.map((fn, idx) => (
                  <div key={idx}>
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-smartify-muted">Posição {idx + 1}</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {SELECTABLE_FUNCTIONS.map((f) => (
                        <Card key={f} active={fn === f} onClick={() => setSlot(idx, f)} compact>
                          <span className="text-sm text-smartify-ink">{SLOT_LABEL[f]}</span>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {step === 'acabamento' && (
              <div>
                <p className="mb-3 text-sm text-smartify-muted">Acabamento do espelho.</p>
                <div className="grid grid-cols-3 gap-3">
                  {COLORS.map((c) => (
                    <Card key={c} active={color === c} onClick={() => goResolve(c)}>
                      <div className="flex flex-col items-center gap-2 py-1">
                        <span className="h-12 w-12 rounded-full border-2 border-white shadow-card" style={{ background: swatchHex(c) }} />
                        <span className="text-sm font-medium text-smartify-ink">{c}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {step === 'resultado' && (
              <ResultadoStep
                resolving={resolving}
                result={result}
                onChooseComposed={chooseComposed}
                onChooseAlternative={chooseAlternative}
              />
            )}

            {step === 'modulos' && chosen && (
              <div className="space-y-4">
                <p className="text-sm text-smartify-muted">Escolhe o módulo de cada posição (opcional — podes decidir depois).</p>
                <NeutralQuestion />
                {chosen.slotFunctions.map((slot, idx) => (
                  (slot === 'moldura' || slot === 'outro') ? null : (
                    <ModuloSlot
                      key={idx}
                      cat={cat}
                      idx={idx}
                      slot={slot}
                      value={moduleSel[idx] ?? null}
                      onPick={(tech, sku) => setModuleForSlot(idx, tech, sku)}
                    />
                  )
                ))}
              </div>
            )}

            {step === 'quantidade' && chosen && (
              <div>
                <div className="rounded-smartify border border-smartify-line bg-white p-4">
                  <div className="flex gap-3">
                    <EspelhoPreview sku={chosen.previewSku} slotFunctions={chosen.slotFunctions} className="h-20 w-20 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-smartify-ink">{chosen.label} · {chosen.color}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-smartify-muted">{chosen.cartSku}</p>
                      <AvailabilityBadge inCatalog={chosen.inCatalog} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <Stepper value={qty} onChange={setQty} />
                    <span className="text-sm text-smartify-muted">unidade(s)</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {cat && !err && (
        <div className="flex items-center gap-2 border-t border-smartify-line p-4">
          {step !== 'tamanho' && (
            <button type="button" onClick={back}
              className="rounded-smartify border border-smartify-line px-4 py-3 text-sm font-medium text-smartify-muted hover:text-smartify-ink">← Voltar</button>
          )}
          {step === 'config' && (
            <button type="button" disabled={!allSlotsChosen} onClick={() => setStep('acabamento')}
              className="flex-1 rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-40">Continuar →</button>
          )}
          {step === 'modulos' && (
            <button type="button" onClick={() => setStep('quantidade')}
              className="flex-1 rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90">Continuar →</button>
          )}
          {step === 'quantidade' && (
            <button type="button" onClick={confirm}
              className="flex-1 rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90">Adicionar à divisão</button>
          )}
        </div>
      )}
    </>
  );
}

// ─── Resultado: preview + availability + nearest alternatives ─────────────────

function ResultadoStep({
  resolving, result, onChooseComposed, onChooseAlternative,
}: {
  resolving: boolean;
  result: ResolveResult | null;
  onChooseComposed: () => void;
  onChooseAlternative: (m: NearestMatch) => void;
}) {
  if (resolving || !result) {
    return <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center text-sm text-smartify-muted">A compor a configuração…</div>;
  }
  const { requested, availability, previewSku, nearest } = result;
  const inStock = availability === 'em-catalogo';
  return (
    <div className="space-y-4">
      <div className="rounded-smartify border border-smartify-line bg-white p-4">
        <div className="flex gap-3">
          <EspelhoPreview sku={previewSku} slotFunctions={requested.slotFunctions} className="h-24 w-24 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs text-smartify-muted">{requested.sku}</p>
            <p className="mt-0.5 text-sm font-medium text-smartify-ink">
              {requested.slotFunctions.map((s) => SLOT_LABEL[s]).join(' · ')}
            </p>
            <AvailabilityBadge inCatalog={inStock} />
          </div>
        </div>
        <button type="button" onClick={onChooseComposed}
          className="mt-3 w-full rounded-smartify bg-smartify-brand px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90">
          {inStock ? 'Escolher esta configuração' : 'Encomendar à medida'}
        </button>
      </div>

      {!inStock && nearest.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
            Ou em stock, mais próximo:
          </p>
          <div className="space-y-2">
            {nearest.map((m) => (
              <div key={m.espelho.sku} className="flex items-center gap-3 rounded-smartify border border-smartify-line bg-white p-3">
                <EspelhoPreview sku={m.espelho.sku.replace(/^SYME/, 'SYPE')} slotFunctions={m.espelho.slotFunctions} className="h-14 w-14 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-smartify-ink">{m.espelho.name}</p>
                  <p className="mt-0.5 text-[11px] text-smartify-muted">{m.reasons.join(' · ')}</p>
                </div>
                <button type="button" onClick={() => onChooseAlternative(m)}
                  className="shrink-0 rounded-smartify border border-smartify-brand px-3 py-1.5 text-xs font-semibold text-smartify-brand hover:bg-smartify-brand/5">Escolher</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AvailabilityBadge({ inCatalog }: { inCatalog: boolean }) {
  return (
    <span className={[
      'mt-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
      inCatalog ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700',
    ].join(' ')}>
      {inCatalog ? '● Em catálogo' : '◐ Encomenda especial'}
    </span>
  );
}

// ─── Module selection for one slot (tech + module) ────────────────────────────

function ModuloSlot({
  cat, idx, slot, value, onPick,
}: {
  cat: Catalog;
  idx: number;
  slot: SlotFunction;
  value: { tech: ModuleTech; sku: string } | null;
  onPick: (tech: ModuleTech, sku: string) => void;
}) {
  // Motor slots (estores/cortinas) offer BOTH estores and cortinas modules — per
  // Rafiq's spec ("cortinas → mostrar estores+cortinas, zigbee e wifi").
  const hasNeutral = useWizardStore((s) => s.hasNeutral);
  const fns: SlotFunction[] = slot === 'estores' || slot === 'cortinas' ? ['estores', 'cortinas'] : [slot];
  const techs = useMemo(
    () => [...new Set(fns.flatMap((f) => techsForFunction(cat, f)))],
    [cat, fns],
  );
  const [tech, setTech] = useState<ModuleTech | null>(value?.tech ?? techs[0] ?? null);
  const modules: Modulo[] = useMemo(
    () => (tech ? fns.flatMap((f) => modulesByFunction(cat, f, tech)) : []),
    [cat, fns, tech],
  );

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
        Posição {idx + 1} · {SLOT_LABEL[slot]}
      </p>
      {techs.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {techs.map((t) => {
            // P0-2: block "com neutro" tech unless the customer explicitly
            // confirmed a neutral. Block on 'no'/'unknown'/unanswered (safe
            // direction) — not only 'no', which let the null default slip through.
            const blocked = techNeedsNeutral(t) && hasNeutral !== 'yes';
            return (
              <button key={t} type="button" disabled={blocked} onClick={() => setTech(t)}
                title={blocked ? 'Requer neutro — confirma se a tua instalação tem neutro' : undefined}
                className={[
                  'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
                  blocked ? 'cursor-not-allowed bg-smartify-paper text-smartify-muted/40'
                    : tech === t ? 'bg-smartify-brand text-white' : 'bg-smartify-paper text-smartify-muted hover:text-smartify-ink',
                ].join(' ')}>{TECH_LABEL[t]}{blocked ? ' (s/ neutro)' : ''}</button>
            );
          })}
        </div>
      )}
      {/* P0-7: tech requirement always visible (not a hover tooltip). */}
      {tech && (
        <p className="mb-2 text-[11px] text-smartify-muted">
          {techHint(tech)}
          {techNeedsHub(tech) && (
            <span className="ml-1">
              <InfoHint label="hub?">
                Pequeno aparelho central que liga os módulos Zigbee à rede. Compras uma vez,
                serve a casa toda. Entra no orçamento automaticamente.
              </InfoHint>
            </span>
          )}
        </p>
      )}
      {modules.length === 0 ? (
        <p className="text-xs text-smartify-muted">Sem módulos para esta tecnologia.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {modules.map((m) => (
            <Card key={m.sku} active={value?.sku === m.sku} onClick={() => tech && onPick(tech, m.sku)} compact>
              <p className="text-sm text-smartify-ink">{m.name}</p>
              {m.notes && <p className="mt-0.5 text-[11px] text-smartify-warn">{m.notes}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Preview image with schematic-glyph fallback ──────────────────────────────

function EspelhoPreview({ sku, slotFunctions, className }: {
  sku: string; slotFunctions: SlotFunction[]; className?: string;
}) {
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    setOk(null);
    previewAvailable().then((a) => { if (live) setOk(a ? null : false); });
    return () => { live = false; };
  }, [sku]);

  if (ok === false) {
    return <SchematicGlyph slotFunctions={slotFunctions} className={className} />;
  }
  return (
    <img
      src={previewUrl(sku, '1')}
      alt={sku}
      className={`rounded-smartify border border-smartify-line bg-white object-contain ${className ?? ''}`}
      onError={() => setOk(false)}
    />
  );
}

// Fallback schematic when no generated image is available (e.g. size-1 combos —
// the donor library has no size-1 SKUs — or production where the generator is
// not shipped).
function SchematicGlyph({ slotFunctions, className }: { slotFunctions: SlotFunction[]; className?: string }) {
  const sym: Record<SlotFunction, string> = {
    'botoes-1': '1', 'botoes-2': '2', 'botoes-3': '3', regulador: '◑',
    estores: '⇅', cortinas: '⇄', moldura: '▢', outro: '·',
  };
  return (
    <div className={`grid place-items-center rounded-smartify border border-dashed border-smartify-line bg-smartify-paper ${className ?? ''}`}>
      <div className="flex gap-0.5">
        {slotFunctions.map((s, i) => (
          <span key={i} className="grid h-7 w-5 place-items-center rounded-sm bg-white text-xs text-smartify-muted shadow-sm">{sym[s]}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Shared presentational helpers (local to keep the flow self-contained) ────

function Card({ active, onClick, children, compact }: {
  active?: boolean; onClick: () => void; children: React.ReactNode; compact?: boolean;
}) {
  return (
    <button type="button" onClick={onClick}
      className={[
        'rounded-smartify border text-left transition-colors',
        compact ? 'p-2.5' : 'p-3.5',
        active ? 'border-smartify-brand bg-smartify-brand/5 ring-1 ring-smartify-brand'
          : 'border-smartify-line bg-white hover:border-smartify-brand/50',
      ].join(' ')}>{children}</button>
  );
}

function Stepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))}
        className="grid h-9 w-9 place-items-center rounded-smartify border border-smartify-line text-lg text-smartify-ink hover:border-smartify-brand">−</button>
      <span className="w-8 text-center text-lg font-semibold text-smartify-ink">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        className="grid h-9 w-9 place-items-center rounded-smartify border border-smartify-line text-lg text-smartify-ink hover:border-smartify-brand">+</button>
    </div>
  );
}

function SlotGlyph({ n }: { n: number }) {
  return (
    <span className="flex gap-0.5 rounded border border-smartify-line bg-smartify-paper p-1">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className="h-6 w-2.5 rounded-sm bg-smartify-brand/30" />
      ))}
    </span>
  );
}
